import { styled } from "@linaria/react"
import { AnimatePresence, motion } from "framer-motion"
import { observable } from "mobx"
import { observer } from "mobx-react-lite"
import { useMemo } from "react"

import { AgentMessage } from "@/components/Chat/AgentMessage.tsx"
import { ProjectNameMessage } from "@/components/Chat/NewProject/ProjectNameMessage.tsx"
import { RepositoryUrlMessage } from "@/components/Chat/NewProject/RepositoryUrlMessage.tsx"
import { UnknownMessage } from "@/components/Chat/UnknownMessage.tsx"
import { UserActionMessage } from "@/components/Chat/UserActionMessage.tsx"
import { UserMessage } from "@/components/Chat/UserMessage.tsx"
import { EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { preventTextSelection } from "@/lib/std.ts"
import { ellipsis } from "@/styles/mixins.ts"
import { smallFontSize } from "@/styles/textStyles.ts"

import type { ArchivedMessagesBlockModel } from "@/components/Chat/ArchivedMessagesBlockModel.ts"
import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { AiChatMessageContent, UTaskProcessDetails } from "@/lib/client/generated/TypeDefinitions.ts"
import type { GitHubVersionInfo } from "@/routes/tasks/TaskViewScene/TaskVersionsModel.ts"
import type { IObservableValue } from "mobx"


export const Message = (props: {
  message: ChatMessageModel
  isAgentRunning: IObservableValue<boolean>
  currentVersionCommitId: IObservableValue<string | null>
  successfulVersionCommitIds: IObservableValue<Set<string>>
  gitHubVersionInfo: IObservableValue<GitHubVersionInfo | null>
  runningProcess: UTaskProcessDetails | null
  onVersionCheckout: (commitId: string) => Promise<void>
  onMarkSuccessful: (commitId: string, successful: boolean) => Promise<void>
}) => {
  if (props.message.content.className === "UserAction") {
    return (
      <UserActionMessage
        content={props.message.content as AiChatMessageContent.UserAction}
        isActive={props.message.active}
        timestamp={props.message.timestamp}
      />
    )
  }

  if (props.message.content.className === "ArchivedBlockGroup") {
    return (
      <ArchivedMessagesBlock
        content={props.message.content}
        model={props.message.archivedBlockModel}
        taskId={props.message.taskId}
        currentVersionCommitId={props.currentVersionCommitId}
        successfulVersionCommitIds={props.successfulVersionCommitIds}
        gitHubVersionInfo={props.gitHubVersionInfo}
        runningProcess={props.runningProcess}
        onVersionCheckout={props.onVersionCheckout}
        onMarkSuccessful={props.onMarkSuccessful}
      />
    )
  }

  if (isUserMessage(props.message)) {
    if (props.message.content.className === "Text") {
      return <UserMessage message={props.message} />
    }

    if (props.message.content.className === "RepositoryUrl") {
      return <RepositoryUrlMessage message={props.message} content={props.message.content} />
    }

    if (props.message.content.className === "ProjectName") {
      return <ProjectNameMessage message={props.message} content={props.message.content} />
    }
  }

  if (isAgentMessage(props.message)) {
    return (
      <AgentMessage
        message={props.message}
        isAgentRunning={props.isAgentRunning}
        currentVersionCommitId={props.currentVersionCommitId}
        successfulVersionCommitIds={props.successfulVersionCommitIds}
        runningProcess={props.runningProcess}
        onVersionCheckout={props.onVersionCheckout}
        onMarkSuccessful={props.onMarkSuccessful}
        gitHubVersionInfo={props.gitHubVersionInfo}
      />
    )
  }

  return <UnknownMessage message={props.message} />
}

const ArchivedMessagesBlock = observer((props: {
  taskId: string
  model?: ArchivedMessagesBlockModel
  content: AiChatMessageContent.ArchivedBlockGroup
  currentVersionCommitId: IObservableValue<string | null>
  successfulVersionCommitIds: IObservableValue<Set<string>>
  gitHubVersionInfo: IObservableValue<GitHubVersionInfo | null>
  runningProcess: UTaskProcessDetails | null
  onVersionCheckout: (commitId: string) => Promise<void>
  onMarkSuccessful: (commitId: string, successful: boolean) => Promise<void>
}) => {
  const model = props.model
  const isAgentRunning = useMemo(() => observable.box(false), [])

  if (!model) {
    return null
  }

  const data = model.data.get()
  const isExpanded = model.isExpanded.get()
  const isReallyExpanded = data && isExpanded

  return (
    <Container>
      <Header onClick={model.toggleExpand} onMouseDown={preventTextSelection}>
        <LineLeft />
        <EllipsisContainer>
          <Title>
            {isReallyExpanded
              ? `Hide ${data.length} messages`
              : "Some messages were collapsed due to version checkout"
            }
          </Title>
        </EllipsisContainer>
        <LineRight />
      </Header>
      <AnimatePresence>
        {isReallyExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
          >
            {data ? data.map((it: ChatMessageModel) => {
              return (
                <Message
                  key={it.id}
                  message={it}
                  isAgentRunning={isAgentRunning}
                  currentVersionCommitId={props.currentVersionCommitId}
                  successfulVersionCommitIds={props.successfulVersionCommitIds}
                  gitHubVersionInfo={props.gitHubVersionInfo}
                  runningProcess={props.runningProcess}
                  onVersionCheckout={props.onVersionCheckout}
                  onMarkSuccessful={props.onMarkSuccessful}
                />
              )
            }) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  )
})

const Line = styled.hr`
  height: 1px;
  flex: 1 1 0;
  min-width: 48px;

  border-bottom: none;
`

const LineLeft = styled(Line)`
  margin-left: 24px;
  background: linear-gradient(to right, transparent 0%, var(--separator-color) 100%);
`

const LineRight = styled(Line)`
  margin-right: 24px;
  background: linear-gradient(to left, transparent 0%, var(--separator-color) 100%);
`

const Title = styled.span`
  ${ellipsis};
`

const Header = styled(Horizontal)`
  align-items: center;
  gap: 8px;

  cursor: pointer;

  ${smallFontSize};
  ${ellipsis};

  margin-bottom: 24px;

  color: var(--secondary-text-color);
  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--text-color);
  }
`

const Container = styled(Vertical)`
  margin-bottom: 24px;
`

function isAgentMessage(message: ChatMessageModel) {
  return message.author.className === "Agent"
}

function isUserMessage(message: ChatMessageModel) {
  return message.author.className === "Principal"
}

